import { useState } from "react";
import { debounce } from "lodash";
import {
  EditButton,
  List,
  ShowButton,
  TextField,
  useTable,
} from "@refinedev/antd";
import { format, formatDistanceToNow } from "date-fns";

import { useCustomMutation, useGetIdentity, useUpdate } from "@refinedev/core";
import {
  Space,
  Table,
  Button,
  Form,
  Input,
  Select,
  notification,
  Typography,
  Spin,
  Popover,
} from "antd";
import { apiUrl } from "../../consts";
import UserSearchSelect from "../components/UserSearchSelect";
import { FilterOutlined } from "@ant-design/icons";

const PERFORMANCE_LEVEL_LABELS: Record<string, string> = {
  foundation: "Foundation",
  development: "Development",
  academy: "Academy",
  performance: "Performance",
};

// Managed players are children. A real (HUMAN) account is a "Parent" only if it
// actually manages at least one child; otherwise it's a regular "User".
const classifyUserType = (record: any): string => {
  if (record?.accountType === "MANAGED_PLAYER") return "Child";
  return record?.childCount > 0 ? "Parent" : "User";
};

export const UsersList = () => {
  const { Title } = Typography;
  const [hoveredRow, setHoveredRow] = useState<string>(""); // Track hovered row
  const [impersonatingUserID, setImpersonatingUserID] = useState<string | null>(null);
  const { data: identity } = useGetIdentity<{ role?: string }>();
  const { mutate: impersonate } = useCustomMutation();

  const handleImpersonate = (id: string) => {
    setImpersonatingUserID(id);
    impersonate(
      {
        url: `${apiUrl}/user/${id}/impersonate`,
        method: "post",
        values: {},
      },
      {
        onSuccess: (response: any) => {
          const redirectUrl = response?.data?.redirectUrl;
          if (redirectUrl) window.location.assign(redirectUrl);
          else {
            setImpersonatingUserID(null);
            notification.error({ message: "No redirect URL returned" });
          }
        },
        onError: (error: any) => {
          setImpersonatingUserID(null);
          notification.error({
            message: "Could not log in as user",
            description:
              error?.response?.data?.error || error?.message || "Please try again.",
          });
        },
      },
    );
  };

  const handleMouseEnter = (id: string) => {
    setHoveredRow(id); // Set the row id when mouse enters
  };

  const handleMouseLeave = () => {
    setHoveredRow(""); // Reset the hover state when mouse leaves
  };

  const {
    mutate: updateStaus,
    variables,
    isLoading: statusLoading,
  } = useUpdate({
    resource: "user",
    mutationMode: "optimistic",
    mutationOptions: {
      retry: 3,
      onSuccess: (data) => {
        notification.success({
          message: "Success",
          description: data?.data?.message,
        });
      },
      onError: (error) => {
        console.error("Error updating user status:", error);
      },
    },
  });

  const handleToggleStatus = (id: string, currentStatus: boolean) => {
    // console.log('Current status:', currentStatus);
    updateStaus({
      id,
      values: { isActive: !currentStatus },
    });
  };

  const { tableProps, searchFormProps } = useTable({
    syncWithLocation: true,
    queryOptions: {
      keepPreviousData: false,
      cacheTime: 20,
    },
    onSearch: (values: any) => {
      // console.log("Search being triggered with values:", values);
      return [
        {
          field: "q",
          operator: "contains",
          value: values.q,
        },
        {
          field: "status",
          operator: "eq",
          value: values.status ? values.status.join(",") : undefined,
        },
        {
          field: "userType",
          operator: "eq",
          value: values.userType ? values.userType.join(",") : undefined,
        },
        {
          field: "performanceLevel",
          operator: "eq",
          value: values.performanceLevel
            ? values.performanceLevel.join(",")
            : undefined,
        },
      ];
    },
  });

  const handleSearchChange = debounce(() => {
    searchFormProps.form?.submit();
  }, 500);

  return (
    <div style={{ padding: "4px", position: "relative" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          width: "100%",
          justifyContent: "flex-end",
          alignItems: "flex-start",
        }}
      >
        <Form {...searchFormProps} onValuesChange={handleSearchChange}>
          <Title level={4}>Filter and Search</Title>
          <div style={{ display: "flex", gap: 10 }}>
            <Popover
              placement="bottomRight"
              trigger="click"
              content={
                <>
                  <Form.Item name="status" label="Status">
                    <Select
                      placeholder="Select status"
                      allowClear
                      style={{ width: 200, marginLeft: 22 }}
                      mode="multiple"
                    >
                      <Select.Option value="ACTIVE">Active</Select.Option>
                      <Select.Option value="INACTIVE">Inactive</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="userType" label="User Type">
                    <Select
                      placeholder="Select user type"
                      allowClear
                      style={{ width: 200 }}
                      mode="multiple"
                    >
                      <Select.Option value="NORMAL">Normal user</Select.Option>
                      <Select.Option value="PARENT">Parent</Select.Option>
                      <Select.Option value="CHILD">Child</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="performanceLevel" label="Performance group">
                    <Select
                      placeholder="Select performance group"
                      allowClear
                      style={{ width: 200 }}
                      mode="multiple"
                    >
                      {Object.entries(PERFORMANCE_LEVEL_LABELS).map(
                        ([value, label]) => (
                          <Select.Option key={value} value={value}>
                            {label}
                          </Select.Option>
                        )
                      )}
                    </Select>
                  </Form.Item>
                </>
              }
            >
              <Button icon={<FilterOutlined />}>Filters</Button>
            </Popover>
            <Form.Item name="q">
              <Input placeholder="Search by title or description" />
            </Form.Item>
          </div>
        </Form>
      </div>
      <List>
        <Table {...tableProps} rowKey="id">
          <Table.Column dataIndex="fullName" title={"Name"} />
          <Table.Column
            dataIndex="email"
            title={"Email"}
            width={200}
            ellipsis
            render={(value: any) => (
              <Popover content={value}>
                <span>{value}</span>
              </Popover>
            )}
          />
          <Table.Column
            dataIndex="phone"
            title={"Phone Number"}
            render={(value, record) => (
              <TextField value={value ? value : "N/A"} />
            )}
          />
          <Table.Column
            dataIndex="lastActiveOn"
            title={"Last active"}
            render={(value, record) => (
              <div
                onMouseEnter={() => handleMouseEnter(record.id)}
                onMouseLeave={handleMouseLeave}
              >
                <Popover content={format(value, "MM/dd/yy hh:mm a")}>
                  <span>{formatDistanceToNow(value)} ago</span>
                </Popover>
              </div>
            )}
          />
          <Table.Column
            dataIndex="createdAt"
            title={"Created"}
            render={(value, record) => (
              <div
                onMouseEnter={() => handleMouseEnter(record.id)}
                onMouseLeave={handleMouseLeave}
              >
                <Popover content={format(value, "MM/dd/yy hh:mm a")}>
                  <span>{formatDistanceToNow(value)} ago</span>
                </Popover>
              </div>
            )}
          />
          <Table.Column
            dataIndex="performanceLevel"
            title={"Performance group"}
            render={(value: any) => (
              <TextField value={PERFORMANCE_LEVEL_LABELS[value] ?? "N/A"} />
            )}
          />
          <Table.Column
            dataIndex="accountType"
            title={"Type"}
            render={(_: any, record: any) => (
              <TextField value={classifyUserType(record)} />
            )}
          />
          <Table.Column
            title={"Actions"}
            dataIndex="actions"
            render={(_, record: any) => (
              <Space>
                <EditButton hideText size="small" recordItemId={record.id} />
                <ShowButton hideText size="small" recordItemId={record.id} />
                {identity?.role === "SUPER_ADMIN" && (
                  <Button
                    size="small"
                    loading={impersonatingUserID === record.id}
                    disabled={
                      record.accountType === "MANAGED_PLAYER" ||
                      !record.isActive ||
                      impersonatingUserID !== null
                    }
                    onClick={() => handleImpersonate(record.id)}
                  >
                    Login as user
                  </Button>
                )}
                <Button
                  style={{
                    width: 100,
                    color: record.isActive ? "red" : "green",
                  }}
                  onClick={() => handleToggleStatus(record.id, record.isActive)}
                >
                  {record.isActive ? (
                    statusLoading && variables && variables.id == record.id ? (
                      <Spin />
                    ) : (
                      "Deactivate"
                    )
                  ) : statusLoading &&
                    variables &&
                    variables.id == record.id ? (
                    <Spin />
                  ) : (
                    "Activate"
                  )}
                </Button>
              </Space>
            )}
          />
        </Table>
      </List>
    </div>
  );
};
